/**
 * WHO OWNS A DECISION — the area routing table.
 *
 * Operator, Sep 9 2026: *"Please make the pipeline send questions to the people
 * involved, you keep sending them to me. Like this one … should have gone to
 * Jordan and CC Oliver (all sales related bugs and features go there)."*
 *
 * Until now every pipeline question led to ONE inbox. The reporter led when
 * there was one, and the operator got everything else — including every plan
 * email, which is hardcoded to the operator. So a decision about the sales
 * cockpit went to the operator because the operator happened to file it, and
 * the person who actually runs that surface never saw it.
 *
 * An area is matched by the ROUTE the bug was filed from (the same
 * `sectionOf` path the intake queue is already keyed by) or by the reporter's
 * own area pick. The route leads because it is a FACT about where the bug is,
 * where the picker is a self-reported dropdown that is often left on its
 * default — but a bug filed from a dashboard about a sales number is real, so
 * the picker is honoured as a fallback rather than ignored.
 *
 * ADDRESSES ARE NEVER TYPED HERE. They come from `lib/announce/recipients.ts`,
 * where every one was verified against the live ACL user store — that file's
 * own warning is the rule this module follows: *"a guessed address is a
 * disclosure, not a typo."* A key that is not in the roster throws at module
 * load rather than silently addressing nobody.
 *
 * PURE and CLIENT-SAFE: `lib/bug-pipeline.ts` imports it and the board bundles
 * that, so no env, no KV, no server-only import.
 */

import { ANNOUNCE_RECIPIENTS } from '@/lib/announce/recipients'

/** One verified address, by roster key. Throws on an unknown key — a routing
 *  table that addresses a mailbox nobody owns is worse than no table. */
function addressOf(key: string): string {
  const found = ANNOUNCE_RECIPIENTS.find((r) => r.key === key)
  if (!found) {
    throw new Error(
      `bug-pipeline-owners: "${key}" is not in ANNOUNCE_RECIPIENTS — add it there (VERIFIED against `
      + 'the live ACL user store) before routing decisions to it',
    )
  }
  return found.email
}

export interface AreaOwner {
  /** Stable id — what a card/test names, never the person's address. */
  key: string
  /** Human label for the surface this covers. */
  label: string
  /** The person who owns decisions here. They LEAD the email. */
  to: string
  /** Copied on every question about this area. */
  cc: readonly string[]
  /** Route prefixes (matched against `sectionOf`'s pathname). */
  routes: readonly string[]
  /** Values from the bug's own area picker that also land here. */
  areas: readonly string[]
  /** WHY it routes here, in the operator's words — so a later reader can check
   *  the rule against the decision rather than guess at intent. */
  why: string
}

export const AREA_OWNERS: readonly AreaOwner[] = [
  {
    key: 'sales',
    label: 'Sales — the cockpit, the door desk, everything under /sales',
    to: addressOf('jordan'),
    cc: [addressOf('oliver')],
    routes: ['/sales'],
    areas: ['Sales'],
    why: 'Operator, Sep 9 2026: "all sales related bugs and features go there" — '
      + 'Jordan leads, Oliver copied.',
  },
]

/**
 * The owner for one bug, or null when nothing claims it (and the old rule
 * stands: the reporter leads, the operator is copied).
 *
 * Route FIRST, picker second — see the module note. A Data Intelligence
 * section (`ws=…&sec=…`) carries no pathname, so those bugs resolve by their
 * picked area; DI lives under /sales, and its own route form is handled by the
 * `dataIntelligence` flag the caller passes.
 */
/**
 * The pathname a bug was filed from.
 *
 * Deliberately NOT `sectionOf` (lib/bug-queues.ts): a VALUE import of that
 * module from lib/bug-pipeline.ts is a runtime cycle, and its id-normalisation
 * (`/batch/:id`) buys nothing for a prefix test. This reads the path and
 * nothing else — a strictly smaller job, so the two cannot disagree about
 * anything this function is asked.
 */
export function routeOfBug(bug: {
  currentRoute?: string | null
  currentUrl?: string | null
}): string {
  const route = (bug.currentRoute ?? '').trim()
  if (route.startsWith('/')) return route.replace(/[?#].*$/, '').replace(/\/+$/, '') || '/'
  const url = (bug.currentUrl ?? '').trim()
  if (!url) return ''
  try {
    return new URL(url, 'https://x.invalid').pathname.replace(/\/+$/, '') || '/'
  } catch {
    return ''
  }
}

export function resolveAreaOwner(bug: {
  currentRoute?: string | null
  currentUrl?: string | null
  /** The reporter's own area pick (`affectedArea`). */
  area?: string | null
}): AreaOwner | null {
  const path = routeOfBug(bug)
  if (path.startsWith('/')) {
    for (const owner of AREA_OWNERS) {
      // Prefix match on a SEGMENT boundary: `/sales` owns `/sales/now`, and
      // must never swallow a hypothetical `/salesforce-import`.
      if (owner.routes.some((r) => path === r || path.startsWith(`${r}/`))) return owner
    }
  }
  // The picker is a fallback, not the primary: it is a self-reported dropdown
  // often left on its default. But a bug filed from a dashboard ABOUT a sales
  // number is real, so it is honoured when the path claims nothing.
  const area = (bug.area ?? '').trim().toLowerCase()
  if (area) {
    for (const owner of AREA_OWNERS) {
      // `startsWith` because the picker stores compound values like
      // "Sales → Now (store drill) / Doors — account activity log".
      if (owner.areas.some((a) => area === a.toLowerCase() || area.startsWith(`${a.toLowerCase()} `))) {
        return owner
      }
    }
  }
  return null
}
